import { Link } from 'react-router';

import { useSoftwareEscrow } from 'src/context/software-escrow';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { AgreementFormEnum, EscrowPartyTypeEnum } from 'src/graphql/schema';
import { BeneficiaryInvitation } from 'src/containers/Escrow/BeneficiaryInvitation';
import { EscrowBeneficiaryCore } from 'src/containers/Escrow/Beneficiary';

const agreementFormContentMap = {
  [AgreementFormEnum.Bipartite]: {
    title: 'Beneficiary information and representatives',
    description: 'Enter the beneficiary organization details and add their key points of contact for this escrow.',
    hintText: (
      <>
        Need a different signing method or a hardcopy agreement?{' '}
        <Link to="/help-resources/contact-us" className="text-accent-software-escrow underline!">
          Contact us
        </Link>
        .
      </>
    ),
    noteText: undefined,
  },
  [AgreementFormEnum.Tripartite]: {
    creator: {
      title: 'Beneficiary information and representatives',
      description:
        'Check your organization details and add the key points of contact and authorized signatories for this escrow.',
      noteText:
        'Once all parties approve the escrow, we’ll send e-signature requests via DropBox Sign to the designated signatories to make the agreement legally binding.',
      hintText: (
        <>
          Need a different signing method or a hardcopy agreement?{' '}
          <Link to="/help-resources/contact-us" className="text-accent-software-escrow underline!">
            Contact us
          </Link>
          .
        </>
      ),
    },
    invitation: {
      title: 'Beneficiary information',
      description:
        'Add the beneficiary organization and its primary point of contact. The beneficiary is the entity authorized to request the release of escrowed material in the event of a release trigger.',
      noteText:
        'Note: The primary contact will be invited to the platform, and be able to add members, representatives and signatories for their organization.',
      hintText: undefined,
    },
  },
};

const getBeneficiaryContent = (agreementForm: AgreementFormEnum, isEscrowInvitation: boolean) => {
  if (agreementForm === AgreementFormEnum.Tripartite)
    return agreementFormContentMap.tripartite[isEscrowInvitation ? 'invitation' : 'creator'];

  return agreementFormContentMap.bipartite;
};

const BeneficiaryContent = () => {
  const {
    softwareEscrow: { id: escrowId, agreement, beneficiary, escrowInvitation, policy, currentCompanyRole },
  } = useSoftwareEscrow();

  if (!beneficiary && agreement.agreementForm === AgreementFormEnum.Tripartite) {
    return <BeneficiaryInvitation escrowId={escrowId} invitationId={escrowInvitation?.id ?? null} />;
  }

  return (
    <EscrowBeneficiaryCore
      beneficiaryId={beneficiary?.id ?? ''}
      escrowId={escrowId}
      agreementType={agreement.agreementForm!}
      canCreateBeneficiary={policy.createBeneficiary}
      canChangeBeneficiary={currentCompanyRole !== EscrowPartyTypeEnum.Beneficiary}
    />
  );
};

const SoftwareEscrowOnboardingBeneficiary = () => {
  const {
    softwareEscrow: { agreement, currentCompanyRole },
  } = useSoftwareEscrow();

  const isEscrowInvitation =
    currentCompanyRole !== EscrowPartyTypeEnum.Beneficiary && agreement.agreementForm === AgreementFormEnum.Tripartite;
  const { title, description, noteText, hintText } = getBeneficiaryContent(
    agreement.agreementForm!,
    isEscrowInvitation,
  );

  return (
    <SoftwareOnboardingMain
      subtitle="beneficiary"
      title={title}
      description={description}
      noteText={noteText}
      hintText={hintText}
    >
      <div className="max-w-260 w-full mx-auto space-y-4">
        <BeneficiaryContent />
      </div>
    </SoftwareOnboardingMain>
  );
};

export default SoftwareEscrowOnboardingBeneficiary;
